/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import '../../styles/Navbar.css';
import '../../styles/ApplicationReview.css';
import '../../styles/ManageProgramData.css';
import useFacility from '../../hooks/useFacility';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import ChamberAssignDialog from './ChamberAssignDialog';
import ResourceAssignDialog from './ResourceAssignDialog';
import { toast } from 'react-toastify';
import ProjectRenewal from './ProjectRenewal';
import SingleApplicationReview from './SingleApplicationReview';
import useResizableColumns from '../../hooks/useResizableColumns';
import { formatDateSafe, safeGet } from '../../utils/formatUtils';
import { getUserDisplayName, getUserDisplayEmail, isUserDeleted } from '../../utils/userUtils';

// Error Boundary Component for catching render errors
class ApplicationReviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ApplicationReview Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">Something went wrong loading the applications.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </Typography>
          </Alert>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              this.setState({ hasError: false, error: null, errorInfo: null });
              window.location.reload();
            }}
          >
            Reload Page
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
            If this problem persists, please contact support.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Separate memoized component for the admin action dialog
const AdminActionDialog = memo(({ 
  open, 
  selectedAction, 
  adminComment, 
  onCommentChange, 
  onConfirm, 
  onCancel 
}) => {
  const handleDialogClose = (event, reason) => {
    // Prevent closing on backdrop click or escape key while typing
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onCancel();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        {selectedAction === 'Approved' ? 'Approve Application' :
         selectedAction === 'Rejected' ? 'Reject Application' : 'Request Changes'}
      </DialogTitle>
      <DialogContent>
        <TextField
          multiline
          rows={6}
          fullWidth
          label="Admin Comments"
          value={adminComment}
          onChange={onCommentChange}
          variant="outlined"
          margin="normal"
          required={selectedAction !== 'Approved'}
          placeholder={
            selectedAction === 'Approved' 
              ? 'Optional: Add any approval notes...' 
              : selectedAction === 'Rejected'
              ? 'Required: Please explain why this application is being rejected...'
              : 'Required: Please specify what changes are needed...'
          }
          helperText={
            selectedAction !== 'Approved' 
              ? 'Comments are required for rejection and revision requests'
              : 'Comments are optional for approvals'
          }
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="primary" 
          variant="contained"
          disabled={!adminComment.trim() && selectedAction !== 'Approved'}
        >
          Confirm {selectedAction === 'Approved' ? 'Approval' : 
                  selectedAction === 'Rejected' ? 'Rejection' : 'Changes Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

// A "Revised" application (a revision-required app that the PI resubmitted) is
// surfaced inside the "Revision Required" tab so admins review the whole
// revision cycle in one place. Its actual status ("Revised") is still shown.
const statusMatchesTab = (appStatus, tabKey) => {
  if (tabKey === 'Revision Required') {
    return appStatus === 'Revision Required' || appStatus === 'Revised';
  }
  return appStatus === tabKey;
};

const ApplicationReview = ({ user }) => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  const location = useLocation();
  const deepLinkHandledRef = useRef(null);
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Drag the dividers in the header to set column widths; the browser
  // remembers them per person.
  const tableRef = useResizableColumns('admin-application-review');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [activeStatus, setActiveStatus] = useState('Submitted'); // Default to Submitted
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facilityFilter, setFacilityFilter] = useState('all');
  const [adminActionDialogOpen, setAdminActionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [chatRefreshTrigger, setChatRefreshTrigger] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setFacilityFilter(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);

  // Status tabs configuration
  const statusTabs = [
    { key: 'Saved', label: 'Saved', count: 0 },
    { key: 'Submitted', label: 'Submitted', count: 0 },
    { key: 'Revision Required', label: 'Revision Required', count: 0 },
    { key: 'Approved', label: 'Approved', count: 0 },
    { key: 'In Progress', label: 'In Progress', count: 0 },
    { key: 'Requires Extension', label: 'Requires Extension', count: 0 },
    { key: 'Completed', label: 'Completed', count: 0 },
    { key: 'Rejected', label: 'Rejected', count: 0 },
    { key: 'System Removal', label: 'System Removal', count: 0 }
  ];

  useEffect(() => {
    fetchApplications();
    fetchFacilities();
  }, []);

  useEffect(() => {
    let filtered = applications;

    // Filter by active status first (Revision Required tab also shows Revised)
    filtered = filtered.filter(app => statusMatchesTab(app.project_status, activeStatus));

    // Text search filter (project_id, title, user names, email)
    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(app =>
        app.project_id?.toLowerCase().includes(q) ||
        app.project_title?.toLowerCase().includes(q) ||
        app.users?.some(user =>
          `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(q) ||
          user.email?.toLowerCase().includes(q)
        )
      );
    }

    // Date range filter
    if (startDateFilter && endDateFilter) {
      filtered = filtered.filter(app => {
        // Skip apps with missing dates
        if (!app.project_start_date || !app.project_end_date) return true;
        
        const appStartDate = new Date(app.project_start_date);
        const appEndDate = new Date(app.project_end_date);
        const filterStartDate = new Date(startDateFilter);
        const filterEndDate = new Date(endDateFilter);
        
        // Check for valid dates
        if (isNaN(appStartDate.getTime()) || isNaN(appEndDate.getTime())) return true;
        
        return appStartDate >= filterStartDate && appEndDate <= filterEndDate;
      });
    }

    // Facility filter
    if (facilityFilter !== 'all') {
      filtered = filtered.filter(app => app.facility?._id === facilityFilter);
    }

    // Application type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(app => (app.application_type || 'project') === typeFilter);
    }

    setFilteredApplications(filtered);
    setPage(0);
  }, [applications, searchQuery, startDateFilter, endDateFilter, activeStatus, facilityFilter, typeFilter]);

  // Reset pagination when status changes
  useEffect(() => {
    setPage(0);
  }, [activeStatus]);

  // Deep link from a notification click: /admin/application?applicationId=<id>
  // opens that project's review page. An explicit &status=<tab> wins; otherwise
  // the tab follows the project's own current status, so going Back to List
  // lands the admin where the project actually sits. Handled once per target so
  // returning to the list doesn't force it open again.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const appId = params.get('applicationId');
    const status = params.get('status');
    if (!appId) return;
    // Once consumed, stop reacting so the admin can freely switch tabs / go
    // back to the list without the deep link forcing things open again.
    if (deepLinkHandledRef.current === appId) return;
    if (status) setActiveStatus(status);
    if (applications.length > 0) {
      const found = applications.find(a => a._id === appId);
      if (found) {
        setSelectedApplication(found);
        if (!status) {
          const tab = statusTabs.find(t => statusMatchesTab(found.project_status, t.key));
          if (tab) setActiveStatus(tab.key);
        }
      }
      deepLinkHandledRef.current = appId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, applications]);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/applications/review');
      setApplications(response.data);
      setFilteredApplications(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to fetch applications. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleStatusChange = useCallback(async (applicationId, newStatus, currentComment = '') => {
    try {
      // Validate comments for rejection and revision requests
      if (!currentComment.trim() && (newStatus === 'Rejected' || newStatus === 'Request Changes')) {
        toast.error('Please provide admin comments before rejecting or requesting changes');
        return;
      }

      // Determine final status
      let finalStatus = newStatus;
      if (newStatus === 'Request Changes') {
        finalStatus = 'Revision Required';
      }

      // Update application status
      await axios.patch(`/api/admin/applications/${applicationId}/status`, {
        status: finalStatus,
        admin_comments: currentComment.trim() ? {
          comment: currentComment.trim(),
          timestamp: new Date()
        } : undefined
      });
      
      toast.success(`Application ${finalStatus.toLowerCase()} successfully`);
      
      // Clear form and close dialog
      setAdminComment('');
      setSelectedApplication(null);
      setAdminActionDialogOpen(false);
      setSelectedAction(null);
      
      // Refresh applications list
      await fetchApplications();
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status');
    }
  }, [fetchApplications]);

  const handleAssignChambers = (application) => {
    setSelectedApplication(application);
    setAssignmentDialogOpen(true);
  };

  const handleAssignResources = (application) => {
    setSelectedApplication(application);
    setResourceDialogOpen(true);
  };

  const handleRenewProject = (application) => {
    setSelectedApplication(application);
    setRenewalDialogOpen(true);
  };

  const handleSubmitApplication = async (application) => {
    try {
      // Basic validation checks
      if (!application.users || application.users.length === 0) {
        toast.error('Cannot submit: Application must have at least one person');
        return;
      }
      
      // Check for PI (role can be string or object)
      const hasPI = application.users.some(user => {
        const roleValue = typeof user.role === 'object' ? user.role?.role : user.role;
        return roleValue === 'PI';
      });

      if (!hasPI) {
        toast.error('Cannot submit: Application must have at least one PI (Principal Investigator)');
        return;
      }
      
      if (!application.safety_training_verified) {
        toast.error('Cannot submit: Safety training verification is required');
        return;
      }
      
      // Confirmation dialog
      const piUser = application.users.find(user => {
        const roleValue = typeof user.role === 'object' ? user.role?.role : user.role;
        return roleValue === 'PI';
      });
      const userName = piUser ? getUserDisplayName(piUser) : 'the user';
      
      const confirmed = window.confirm(
        `Are you sure you want to submit this application on behalf of ${userName}?\n\n` +
        `This will change the status from "Saved" to "Submitted" and the application will enter the review process.`
      );
      
      if (!confirmed) {
        return;
      }
      
      // Update application status from 'Saved' to 'Submitted'
      await axios.patch(`/api/admin/applications/${application._id}/status`, {
        status: 'Submitted',
        admin_comments: {
          comment: 'Application submitted by admin on behalf of user',
          timestamp: new Date()
        }
      });
      
      toast.success('Application submitted successfully');
      
      // Refresh applications list
      await fetchApplications();
      
      // Go back to list view
      setSelectedApplication(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.error || 'Failed to submit application');
    }
  };

  const handleAssignmentSave = (updatedProject) => {
    setChatRefreshTrigger((k) => k + 1);
    if (!updatedProject) {
      if (selectedApplication) {
        handleApplicationUpdate(selectedApplication._id);
      }
      return;
    }
    setApplications(prevApplications =>
      prevApplications.map(app =>
        app._id === updatedProject._id ? updatedProject : app
      )
    );
    if (selectedApplication && selectedApplication._id === updatedProject._id) {
      setSelectedApplication(updatedProject);
    }
  };

  const handleApplicationUpdate = async (applicationId) => {
    try {
      // Fetch the updated application data
      const response = await axios.get(`/api/projects/${applicationId}`);
      const updatedApplication = response.data;
      
      // Update the applications list
      setApplications(prevApplications => 
        prevApplications.map(app => 
          app._id === applicationId ? updatedApplication : app
        )
      );
      
      // Update the selected application if it's the one being updated
      if (selectedApplication && selectedApplication._id === applicationId) {
        setSelectedApplication(updatedApplication);
      }
      
    } catch (error) {
      console.error('Error refreshing application data:', error);
      toast.error('Failed to refresh application data');
    }
  };

  const handleAdminAction = (action) => {
    setSelectedAction(action);
    setAdminActionDialogOpen(true);
  };

  const handleConfirmAction = useCallback(() => {
    if (selectedApplication && selectedAction) {
      handleStatusChange(selectedApplication._id, selectedAction, adminComment);
    }
  }, [selectedApplication, selectedAction, handleStatusChange, adminComment]);

  const handleCommentChange = useCallback((e) => {
    setAdminComment(e.target.value);
  }, []);

  const handleDialogCancel = useCallback(() => {
    setAdminActionDialogOpen(false);
    setAdminComment('');
    setSelectedAction(null);
  }, []);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, filteredApplications.length);
  const paginatedApplications = filteredApplications.slice(startIndex, endIndex);

  if (loading) {
    return <div className="loading">Loading applications...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
        <button onClick={fetchApplications}>Retry</button>
      </div>
    );
  }

  return (
    <>
      {selectedApplication ? (
        <div className="admin-applications">
          <SingleApplicationReview
            application={selectedApplication}
            user={user}
            chatRefreshTrigger={chatRefreshTrigger}
            onBack={() => {
              setSelectedApplication(null);
              fetchApplications();
            }}
            onApprove={() => handleAdminAction('Approved')}
            onReject={() => handleAdminAction('Rejected')}
            onRequestChanges={() => handleAdminAction('Request Changes')}
            onAssignChambers={() => handleAssignChambers(selectedApplication)}
            onAssignResources={() => handleAssignResources(selectedApplication)}
            onRenewProject={() => handleRenewProject(selectedApplication)}
            onSubmit={() => handleSubmitApplication(selectedApplication)}
            onEndProject={() => {
              fetchApplications();
              setSelectedApplication(null);
            }}
            onApplicationUpdate={handleApplicationUpdate}
          />
        </div>
      ) : (
        <div className="manage-program-container application-review-container">
          <div className="sidebar">
            <h2>Project Review</h2>
            <ul>
              {statusTabs.map((tab) => {
                const base = facilityFilter !== 'all'
                  ? applications.filter(app => app.facility?._id === facilityFilter)
                  : applications;
                const count = base.filter(app => statusMatchesTab(app.project_status, tab.key)).length;
                return (
                  <li key={tab.key}>
                    <button
                      type="button"
                      onClick={() => setActiveStatus(tab.key)}
                      className={activeStatus === tab.key ? 'active' : ''}
                      aria-current={activeStatus === tab.key ? 'page' : undefined}
                    >
                      {tab.label} ({count})
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          
          <div className="content-area">
            <div className="filters-section">
              <div className="search-filters">
                <TextField
                  label="Search"
                  placeholder="Proposal ID, title, or user..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mr: 2 }}
                />
                
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDateFilter}
                  onChange={(e) => setStartDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ mr: 2 }}
                />
                
                <TextField
                  label="End Date"
                  type="date"
                  value={endDateFilter}
                  onChange={(e) => setEndDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ mr: 2 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
                  <InputLabel>Facility</InputLabel>
                  <Select
                    value={facilityFilter}
                    onChange={(e) => setFacilityFilter(e.target.value)}
                    label="Facility"
                  >
                    <MenuItem value="all">All Facilities</MenuItem>
                    {facilities.map((facility) => (
                      <MenuItem key={facility._id} value={facility._id}>
                        {facility.facility_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140, mr: 2 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="project">Project Applications</MenuItem>
                    <MenuItem value="resource_only">Resource Only</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className="applications-table">
              <h2>{activeStatus} Applications</h2>
              <table ref={tableRef} className="resizable-table">
                <thead>
                  <tr>
                    {/* Proposal ID is internal — see the note in
                        UnifiedProjects. Still searchable, still on the project's
                        own page and on the review screen. */}
                    <th className="col-project-title">Title</th>
                    <th>Type</th>
                    <th>Submitted By</th>
                    <th className="col-facility">Facility</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedApplications.map((app) => {
                    try {
                      return (
                        <tr key={app._id}>
                          <td className="col-project-title">{app.project_title || 'Untitled'}</td>
                          <td>
                            {app.application_type === 'resource_only' ? (
                              <Chip label="Resource" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.75rem' }} />
                            ) : (
                              <Chip label="Project" size="small" sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600, fontSize: '0.75rem' }} />
                            )}
                          </td>
                          <td style={isUserDeleted(app.users?.[0]) ? { color: '#856404', fontStyle: 'italic' } : {}}>
                            {app.users?.[0] ? getUserDisplayEmail(app.users[0]) : 'No email'}
                          </td>
                          <td className="col-facility">{app.facility?.facility_name || 'Not specified'}</td>
                          <td>{formatDateSafe(app.project_start_date)}</td>
                          <td>{formatDateSafe(app.project_end_date)}</td>
                          <td>{app.project_status || 'Unknown'}</td>
                          <td>
                            <button 
                              className="review-btn"
                              onClick={() => setSelectedApplication(app)}
                            >
                              Review
                            </button>
                          </td>
                        </tr>
                      );
                    } catch (error) {
                      console.error('Error rendering application row:', app._id, error);
                      return (
                        <tr key={app._id || Math.random()}>
                          <td colSpan="8" style={{ color: 'red', textAlign: 'center' }}>
                            Error displaying application: {app.project_id || app._id || 'Unknown'}
                          </td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <span>Show</span>
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    sx={{ mx: 1 }}
                    size="small"
                  >
                    {[10, 25, 50, 100].map(n => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                  <span>entries</span>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton aria-label="First page" onClick={() => handleChangePage(0)} disabled={page === 0}>
                    <FirstPage />
                  </IconButton>
                  <IconButton aria-label="Previous page" onClick={() => handleChangePage(page - 1)} disabled={page === 0}>
                    <KeyboardArrowLeft />
                  </IconButton>
                  <Box sx={{ mx: 2 }}>
                    Page {page + 1} of {Math.ceil(filteredApplications.length / rowsPerPage)}
                  </Box>
                  <IconButton aria-label="Next page"
                    onClick={() => handleChangePage(page + 1)}
                    disabled={page >= Math.ceil(filteredApplications.length / rowsPerPage) - 1}
                  >
                    <KeyboardArrowRight />
                  </IconButton>
                  <IconButton aria-label="Last page"
                    onClick={() => handleChangePage(Math.ceil(filteredApplications.length / rowsPerPage) - 1)}
                    disabled={page >= Math.ceil(filteredApplications.length / rowsPerPage) - 1}
                  >
                    <LastPage />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ mt: 2, color: 'text.secondary' }}>
                Showing {startIndex + 1} to {endIndex} of {filteredApplications.length} entries
                {searchQuery && ` (filtered from ${applications.length} total entries)`}
              </Box>
            </div>
          </div>
        </div>
      )}

      <ChamberAssignDialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        application={selectedApplication}
        onSave={handleAssignmentSave}
      />

      <ResourceAssignDialog
        open={resourceDialogOpen}
        onClose={() => setResourceDialogOpen(false)}
        application={selectedApplication}
        onSave={handleAssignmentSave}
      />

      <ProjectRenewal
        open={renewalDialogOpen}
        onClose={() => setRenewalDialogOpen(false)}
        projectId={selectedApplication?._id}
      />

      <AdminActionDialog 
        open={adminActionDialogOpen}
        selectedAction={selectedAction}
        adminComment={adminComment}
        onCommentChange={handleCommentChange}
        onConfirm={handleConfirmAction}
        onCancel={handleDialogCancel}
      />
    </>
  );
};

// Wrap the component with error boundary
const ApplicationReviewWithErrorBoundary = (props) => (
  <ApplicationReviewErrorBoundary>
    <ApplicationReview {...props} />
  </ApplicationReviewErrorBoundary>
);

export default ApplicationReviewWithErrorBoundary;